import { Button } from "primereact/button";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { Menu } from "primereact/menu";
import { MultiSelect } from "primereact/multiselect";
import { InputSwitch } from "primereact/inputswitch";
import { Toast } from "primereact/toast";
import React, { useEffect, useRef, useState } from "react";
import Http from "../../../Common/Http";
import IsLogin from "../../../Common/IsLogin";
import { isPrivilegedUser } from "../../../Common/permissions";
import TaskDetails from "../TaskCalendar/TaskDetails";
import CreateStory from "../Story/Create";
import EditStory from "../Story/Edit";
import CreateTask from "../Task/Create";
import EditTask from "../Task/Edit";
import StatusUpdate from "../Task/StatusUpdate";

// CSS for status dropdowns
const statusDropdownStyle = `
  .status-dropdown-container .p-dropdown {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100px;
    border: none;
    padding: 0;
  }
  
  .status-dropdown-container .p-dropdown-label {
    padding: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    /* inherit so the pill's own status colour shows through */
    color: inherit;
    font-weight: 600;
    line-height: 1;
    font-size: 11px;
    text-align: center;
  }
  
  .status-dropdown-container .p-dropdown-trigger {
    display: none;
  }
  
  .status-dropdown-container .p-dropdown-panel {
    margin-top: 5px;
    box-shadow: 0 3px 10px rgba(0,0,0,0.2);
    border-radius: 4px;
  }
  
  .status-dropdown-container .p-dropdown-panel .p-dropdown-items {
    padding: 4px;
  }
  
  .status-dropdown-container .p-dropdown-panel .p-dropdown-item {
    padding: 4px 8px;
    margin: 2px 0;
    border-radius: 3px;
    font-size: 12px;
    text-align: center;
  }
  
  .status-dropdown-container .p-dropdown:focus,
  .status-dropdown-container .p-dropdown:hover {
    box-shadow: 0 0 0 3px rgba(15, 23, 42, 0.06);
    outline: none;
  }

  .status-dropdown-container .p-dropdown {
    transition: all 0.2s ease;
  }

  /* Column-based backlog layout */
  .backlog-container {
    overflow: auto;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    background: #fff;
  }

  .backlog-item {
    transition: background-color 0.15s ease;
    border-bottom: 1px solid #f1f5f9;
  }

  .task-description {
    margin-left: 60px !important;
    padding-left: 0 !important;
  }

  /* ---- Column header ---- */
  .pm-backlog-head {
    display: flex;
    padding: 8px 16px;
    background: #f8fafc;
    border-bottom: 1px solid #e2e8f0;
    font-weight: 600;
    font-size: 0.7rem;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: #64748b;
    position: sticky;
    top: 0;
    z-index: 5;
  }

  /* ---- Rows ---- */
  .backlog-row-story {
    background: #f8fafc;
    border-left: 3px solid #0747A6;
  }
  .backlog-row-story:hover {
    background: #f1f5f9;
  }

  .backlog-row-task {
    background: #fff;
  }
  .backlog-row-task:hover {
    background: #f8fafc;
  }

  /* ---- Toolbar ---- */
  /* ------------------------------------------------------------------
     Toolbar. Deliberately minimal: sizing is left to PrimeReact's own
     p-inputtext-sm on the controls, so InputText / Dropdown / MultiSelect all
     resolve to the same height without any padding overrides here. This block
     only does layout.
     ------------------------------------------------------------------ */
  .pm-toolbar {
    display: flex;
    flex-wrap: nowrap;
    align-items: flex-end;
    gap: 10px;
    padding: 12px 14px;
    margin-bottom: 16px;
    background: #fff;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
  }

  .pm-toolbar-group {
    display: flex;
    flex-wrap: nowrap;
    align-items: flex-end;
    gap: 10px;
    min-width: 0;
  }

  /* Filters absorb the free width; the right-hand controls keep their size. */
  .pm-toolbar-group:first-child { flex: 1 1 auto; }

  .pm-toolbar-end {
    flex: 0 0 auto;
    margin-left: auto;
    align-items: center;
    padding-bottom: 3px;
  }

  .pm-field {
    display: flex;
    flex-direction: column;
    gap: 4px;
    flex: 1 1 0;
    min-width: 120px;
  }

  .pm-field-search { flex: 1.5 1 0; min-width: 140px; }

  .pm-field-label {
    font-size: 0.68rem;
    font-weight: 600;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: #94a3b8;
    margin: 0;
  }

  .pm-field .p-dropdown,
  .pm-field .p-multiselect,
  .pm-field .p-inputtext {
    width: 100%;
  }

  /* The theme's 3rem trigger is a lot of dead width in a narrow filter. This is
     a width-only change, so it can't disturb the control's layout. */
  .pm-field .p-dropdown-trigger,
  .pm-field .p-multiselect-trigger {
    width: 2rem;
  }

  /* This PrimeReact build ships no rules at all for .p-input-icon-left (it was
     superseded by IconField), so the icon overlay is done by hand. */
  .pm-field-search .p-input-icon-left {
    position: relative;
    display: block;
  }
  .pm-field-search .p-input-icon-left > i {
    position: absolute;
    top: 50%;
    left: 10px;
    margin-top: -0.5rem;
    font-size: 0.8rem;
    color: #94a3b8;
    pointer-events: none;
    z-index: 1;
  }
  .pm-field-search .p-input-icon-left > .p-inputtext {
    padding-left: 30px;
  }

  .pm-switch {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 0.74rem;
    color: #475569;
    margin: 0;
    cursor: pointer;
    white-space: nowrap;
    flex: 0 0 auto;
  }

  .pm-switch .p-inputswitch { width: 34px; height: 18px; }
  .pm-switch .p-inputswitch .p-inputswitch-slider:before {
    width: 12px; height: 12px; left: 3px; margin-top: -6px;
  }
  .pm-switch .p-inputswitch.p-inputswitch-checked .p-inputswitch-slider:before {
    transform: translateX(15px);
  }

  .pm-toolbar-btn { white-space: nowrap; }

  /* ---- Shared chips (mirrors the Task list) ---- */
  .backlog-container .pm-type-icon {
    width: 22px;
    height: 22px;
    border-radius: 6px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 0.7rem;
  }

  .backlog-container .pm-label-chip {
    display: inline-block;
    font-size: 0.65rem;
    font-weight: 600;
    line-height: 1;
    padding: 0.25rem 0.45rem;
    border-radius: 4px;
    border: 1px solid transparent;
    white-space: nowrap;
    vertical-align: middle;
  }

  .backlog-container .pm-avatar {
    width: 26px;
    height: 26px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.62rem;
    font-weight: 600;
    color: #fff;
    border: 2px solid #fff;
  }

  /* Actions stay quiet until the row is hovered */
  .backlog-item .pm-actions {
    opacity: 0.35;
    transition: opacity 0.12s ease;
  }
  .backlog-item:hover .pm-actions {
    opacity: 1;
  }

  /* The single row needs ~778px of content width. With the 240px sidebar and
     30px body padding, a viewport under ~1100px can't provide that, so wrapping
     is re-enabled below that point and only there. Every real laptop (1280+)
     stays on one line. */
  /* The single row needs ~818px of content width. With the 240px fixed sidebar
     and 30px body padding, that needs a viewport of ~1200px. Below it, wrapping
     is allowed rather than letting the row overflow. Laptops (1280+) stay on
     one line. */
  @media (max-width: 1200px) {
    .pm-toolbar,
    .pm-toolbar-group { flex-wrap: wrap; }
    .pm-toolbar-end { margin-left: 0; }
    .pm-field { flex: 1 1 170px; min-width: 170px; }
  }
`;

// #rrggbb (or shorthand) → rgba(), so one stored colour gives both a readable
// foreground and the soft background tint behind it.
const backlogTint = (hex, alpha) => {
  if (!hex) return `rgba(100, 116, 139, ${alpha})`;
  let h = String(hex).replace("#", "");
  if (h.length === 3) h = h.split("").map((c) => c + c).join("");
  if (h.length !== 6) return `rgba(100, 116, 139, ${alpha})`;
  const r = parseInt(h.substring(0, 2), 16);
  const g = parseInt(h.substring(2, 4), 16);
  const b = parseInt(h.substring(4, 6), 16);
  if ([r, g, b].some(Number.isNaN)) return `rgba(100, 116, 139, ${alpha})`;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

// Deterministic avatar colour so a person keeps the same chip everywhere.
const BACKLOG_AVATAR_COLORS = ["#2563eb", "#7c3aed", "#db2777", "#ea580c", "#0891b2", "#16a34a", "#ca8a04", "#dc2626"];

const Backlog = () => {
  // References
  const toast = useRef(null);
  const createMenuRef = useRef(null);

  // State variables
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");

  // Current user state
  const [currentUser, setCurrentUser] = useState(null);
  // When true, only tasks assigned to the current user are shown. Defaults ON
  // for normal members so they land on their own work first.
  const [myTasksOnly, setMyTasksOnly] = useState(false);

  // Server-side filter state
  const [filterProjectId, setFilterProjectId] = useState(null);
  const [filterDeveloperId, setFilterDeveloperId] = useState(null);
  const [filterStatuses, setFilterStatuses] = useState([]);
  const [includeDone, setIncludeDone] = useState(false);

  // Pagination / load-more state
  const [storiesPagination, setStoriesPagination] = useState({ current_page: 1, has_more: false });
  const [tasksPagination, setTasksPagination] = useState({ current_page: 1, has_more: false });
  const [standaloneCounts, setStandaloneCounts] = useState({ total: 0, done: 0 });
  const [loadingMore, setLoadingMore] = useState(false);

  // Lazy-loaded completed tasks per story (active view only)
  const [showCompletedStories, setShowCompletedStories] = useState({});

  // Data states
  const [stories, setStories] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [expandedStories, setExpandedStories] = useState({});
  const [individualTasksExpanded, setIndividualTasksExpanded] = useState(true);
  const [allExpanded, setAllExpanded] = useState(true);

  // State to track highlighted items (newly created or edited)
  const [highlightedItems, setHighlightedItems] = useState({});

  // Dialog visibility states
  const [createStoryVisible, setCreateStoryVisible] = useState(false);
  const [editStoryVisible, setEditStoryVisible] = useState(false);
  const [createTaskVisible, setCreateTaskVisible] = useState(false);
  const [editTaskVisible, setEditTaskVisible] = useState(false);
  const [statusUpdateVisible, setStatusUpdateVisible] = useState(false);
  const [detailsVisible, setDetailsVisible] = useState(false);
  const [detailsTask, setDetailsTask] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);

  // Selection states
  const [selectedStory, setSelectedStory] = useState(null);
  const [selectedTask, setSelectedTask] = useState(null);

  // State for storing full story data for editing
  const [storyDataForEdit, setStoryDataForEdit] = useState(null);

  // State for storing full task data for editing
  const [taskDataForEdit, setTaskDataForEdit] = useState(null);

  // Mapping data
  const [projects, setProjects] = useState({});
  const [labels, setLabels] = useState({});
  const [developers, setDevelopers] = useState({});

  // Status options for task status dropdowns
  const statusOptions = [
    { label: "To Do", value: "To Do" },
    { label: "In Progress", value: "In Progress" },
    { label: "Pause", value: "Pause" },
    { label: "Review", value: "Review" },
    { label: "Done", value: "Done" },
  ];

  // Function to check if a status transition is valid
  const isValidStatusTransition = (currentStatus, newStatus) => {
    // Special handling for Pause status
    if (newStatus === "Pause") {
      // Can only pause if task is in In Progress or Review, not To Do
      return currentStatus === "In Progress" || currentStatus === "Review";
    }

    // Special handling for returning from Pause
    if (currentStatus === "Pause") {
      // Can resume to In Progress or Review
      return newStatus === "In Progress" || newStatus === "Review" || newStatus === "Done";
    }

    // For regular forward progression
    const statusOrder = ["To Do", "In Progress", "Review", "Done"];
    const currentIndex = statusOrder.indexOf(currentStatus);
    const newIndex = statusOrder.indexOf(newStatus);

    // Only allow moving forward in the workflow (skipping Pause in the regular flow)
    return newIndex > currentIndex;
  };

  // Function to check if current user is assigned to a task
  const isUserAssignedToTask = (task) => {
    if (!currentUser || !task || !task.developers) return false;

    // Check if the current user is among the assigned developers
    return task.developers.some((dev) => dev.id === currentUser.id);
  };

  // Admins and department heads get full access; everyone else is restricted.
  const privileged = isPrivilegedUser(currentUser);

  // Whether the current user may change the status of a given task. Normal
  // members can only change the status of tasks assigned to them; editing,
  // copying and deleting are reserved for privileged users.
  const canManageTask = (task) => privileged || isUserAssignedToTask(task);

  // Create menu items
  const createMenuItems = [
    {
      label: "Story",
      icon: "pi pi-book",
      command: () => setCreateStoryVisible(true),
    },
    {
      label: "Task",
      icon: "pi pi-check-square",
      command: () => setCreateTaskVisible(true),
    },
  ];

  // Handle adding a new task without refreshing all data
  const handleAddNewTask = (newTaskData) => {
    if (!newTaskData) return;

    // Add the newly created task to the tasks state
    setTasks((prevTasks) => {
      // Make sure we're not adding a duplicate
      const exists = prevTasks.some((task) => task.id === newTaskData.id);
      if (exists) return prevTasks;

      // Add developer data if it's not included in the newTaskData
      const taskWithDevelopers = {
        ...newTaskData,
        developers: Array.isArray(newTaskData.developers) ? newTaskData.developers : newTaskData.developer_ids?.map((id) => ({ id })) || [],
      };

      // Highlight the newly created task
      highlightItem(taskWithDevelopers.id, "task");

      return [...prevTasks, taskWithDevelopers];
    });
  };

  // Handle updating a task without refreshing all data
  const handleUpdateTask = (updatedTaskData) => {
    if (!updatedTaskData) return;

    setTasks((prevTasks) => {
      return prevTasks.map((task) => {
        if (task.id === updatedTaskData.id) {
          // If we only received partial data (just id and status), merge it with existing task data
          const taskWithUpdates = {
            ...task, // Keep all existing task data
            ...updatedTaskData, // Override with any updated fields
            // Make sure developers are properly structured
            developers: Array.isArray(updatedTaskData.developers)
              ? updatedTaskData.developers
              : updatedTaskData.developer_ids?.map((id) => ({ id })) || task.developers || [],
          };

          // Highlight the updated task
          highlightItem(taskWithUpdates.id, "task");

          return taskWithUpdates;
        }
        return task;
      });
    });
  };

  // Handle adding a new story without refreshing all data
  const handleAddNewStory = (newStoryData) => {
    if (!newStoryData) return;

    // Add the newly created story to the stories state
    setStories((prevStories) => {
      // Make sure we're not adding a duplicate
      const exists = prevStories.some((story) => story.id === newStoryData.id);
      if (exists) return prevStories;

      // Initialize expanded state for the new story
      setExpandedStories((prev) => ({
        ...prev,
        [newStoryData.id]: true,
      }));

      // Highlight the newly created story
      highlightItem(newStoryData.id, "story");

      return [...prevStories, newStoryData];
    });
  };

  // Handle updating a story without refreshing all data
  const handleUpdateStory = (updatedStoryData) => {
    if (!updatedStoryData) return;

    setStories((prevStories) => {
      return prevStories.map((story) => {
        if (story.id === updatedStoryData.id) {
          // Highlight the updated story
          highlightItem(updatedStoryData.id, "story");

          return updatedStoryData;
        }
        return story;
      });
    });
  };

  // Function to highlight an item for 2 seconds
  const highlightItem = (itemId, itemType) => {
    // Set the highlight
    setHighlightedItems((prev) => ({
      ...prev,
      [`${itemType}-${itemId}`]: true,
    }));

    // Remove the highlight after 2 seconds
    setTimeout(() => {
      setHighlightedItems((prev) => {
        const newState = { ...prev };
        delete newState[`${itemType}-${itemId}`];
        return newState;
      });
    }, 2000);
  };

  // State for expanded task descriptions
  const [expandedDescriptions, setExpandedDescriptions] = useState({});

  // Toggle description expansion
  const toggleDescriptionExpansion = (taskId, e) => {
    e.stopPropagation();
    setExpandedDescriptions((prev) => ({
      ...prev,
      [taskId]: !prev[taskId],
    }));
  };

  // State for task types data
  const [taskTypes, setTaskTypes] = useState({});

  // Fetch task types
  const fetchTaskTypes = async () => {
    try {
      const res = await Http.get(`/task-type`);
      const taskTypesMap = {};
      if (res.data && res.data.data) {
        res.data.data.forEach((type) => {
          taskTypesMap[type.id] = {
            name: type.task_type,
            icon: type.icon || "pi pi-check-square",
            color: type.color || "#4CAF50",
          };
        });
      }
      setTaskTypes(taskTypesMap);
    } catch (error) {
      console.error("Error fetching task types:", error);
    }
  };

  // Toggle all sections expanded/collapsed state
  const toggleAllExpanded = () => {
    const newExpandedState = !allExpanded;
    setAllExpanded(newExpandedState);

    // Update stories expanded state
    const updatedExpandedStories = {};
    stories.forEach((story) => {
      updatedExpandedStories[story.id] = newExpandedState;
    });
    setExpandedStories(updatedExpandedStories);

    // Update individual tasks expanded state
    setIndividualTasksExpanded(newExpandedState);
  };

  // Load reference data + current user once
  useEffect(() => {
    fetchCurrentUser();
    Promise.allSettled([fetchProjects(), fetchLabels(), fetchDevelopers(), fetchTaskTypes()]);
  }, []);

  // (Re)load the backlog whenever filters or search change (debounced)
  useEffect(() => {
    const handle = setTimeout(() => {
      fetchData();
    }, 350);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterProjectId, filterDeveloperId, filterStatuses, includeDone, searchTerm]);

  // Build query params for the /backlog endpoint
  const buildBacklogParams = (page = 1, tasksPage = 1) => {
    const params = { page, per_page: 20, tasks_page: tasksPage, tasks_per_page: 20 };
    if (filterProjectId) params.project_id = filterProjectId;
    if (filterDeveloperId) params.developer_id = filterDeveloperId;
    if (filterStatuses.length > 0) params.status = filterStatuses.join(",");
    if (includeDone) params.include_done = true;
    if (searchTerm.trim()) params.search = searchTerm.trim();
    return params;
  };

  // Flatten nested story tasks + standalone tasks into the flat `tasks` array
  // the existing render logic relies on.
  const flattenTasks = (storiesArr, standalone) => {
    const nested = (storiesArr || []).flatMap((s) => s.tasks || []);
    return [...nested, ...(standalone || [])];
  };

  // Fetch current logged in user
  const fetchCurrentUser = async () => {
    try {
      const response = await Http.get("/me");
      if (response.data && response.data.data) {
        const user = response.data.data;
        setCurrentUser(user);
        // Normal members default to viewing only their own tasks.
        if (!isPrivilegedUser(user)) {
          setMyTasksOnly(true);
        }
      }
    } catch (error) {
      console.error("Error fetching current user:", error);
    }
  };

  const fetchData = async () => {
    setLoading(true);
    setError("");

    try {
      const response = await Http.get("/backlog", { params: buildBacklogParams(1, 1) });
      const data = response.data || {};
      const storiesArr = data.stories || [];
      const standalone = data.standalone_tasks || [];

      setStories(storiesArr);
      setTasks(flattenTasks(storiesArr, standalone));
      setStoriesPagination(data.stories_pagination || { current_page: 1, has_more: false });
      setTasksPagination(data.standalone_pagination || { current_page: 1, has_more: false });
      setStandaloneCounts(data.standalone_counts || { total: 0, done: 0 });
      setShowCompletedStories({});

      // Initialize expanded state for stories
      const initialExpandedState = {};
      storiesArr.forEach((story) => {
        initialExpandedState[story.id] = true;
      });
      setExpandedStories(initialExpandedState);
    } catch (error) {
      console.error("Error fetching backlog data:", error);
      setError("Failed to load backlog data. Please refresh the page to try again.");
    } finally {
      setLoading(false);
    }
  };

  // Load the next page of stories and append
  const loadMoreStories = async () => {
    if (loadingMore || !storiesPagination.has_more) return;
    setLoadingMore(true);
    try {
      const nextPage = (storiesPagination.current_page || 1) + 1;
      const response = await Http.get("/backlog", { params: buildBacklogParams(nextPage, 1) });
      const data = response.data || {};
      const newStories = data.stories || [];

      setStories((prev) => [...prev, ...newStories]);
      setTasks((prev) => [...prev, ...flattenTasks(newStories, [])]);
      setStoriesPagination(data.stories_pagination || { current_page: nextPage, has_more: false });
      setExpandedStories((prev) => {
        const next = { ...prev };
        newStories.forEach((s) => {
          next[s.id] = true;
        });
        return next;
      });
    } catch (error) {
      console.error("Error loading more stories:", error);
    } finally {
      setLoadingMore(false);
    }
  };

  // Load the next page of standalone tasks and append
  const loadMoreTasks = async () => {
    if (loadingMore || !tasksPagination.has_more) return;
    setLoadingMore(true);
    try {
      const nextPage = (tasksPagination.current_page || 1) + 1;
      const response = await Http.get("/backlog", { params: buildBacklogParams(1, nextPage) });
      const data = response.data || {};
      const newTasks = data.standalone_tasks || [];

      setTasks((prev) => [...prev, ...newTasks]);
      setTasksPagination(data.standalone_pagination || { current_page: nextPage, has_more: false });
    } catch (error) {
      console.error("Error loading more tasks:", error);
    } finally {
      setLoadingMore(false);
    }
  };

  // Lazy-load a story's completed (Done) tasks on demand
  const toggleStoryCompleted = async (storyId, e) => {
    if (e) e.stopPropagation();
    const currentlyShown = !!showCompletedStories[storyId];
    if (currentlyShown) {
      setShowCompletedStories((prev) => ({ ...prev, [storyId]: false }));
      return;
    }
    try {
      const response = await Http.get("/task", { params: { story_id: storyId, status: "Done" } });
      const doneTasks = response.data?.data || [];
      setTasks((prev) => {
        const existingIds = new Set(prev.map((t) => t.id));
        const merged = doneTasks.filter((t) => !existingIds.has(t.id));
        return [...prev, ...merged];
      });
      setShowCompletedStories((prev) => ({ ...prev, [storyId]: true }));
    } catch (error) {
      console.error("Error loading completed tasks:", error);
    }
  };

  // Fetch projects
  const fetchProjects = async () => {
    try {
      const res = await Http.get(`/project`);
      const projectsMap = {};
      if (res.data && res.data.data) {
        res.data.data.forEach((project) => {
          projectsMap[project.id] = project.title;
        });
      }
      setProjects(projectsMap);
    } catch (error) {
      console.error("Error fetching projects:", error);
    }
  };

  // Fetch labels
  const fetchLabels = async () => {
    try {
      const res = await Http.get(`/label`);
      const labelsMap = {};
      if (res.data && res.data.data) {
        res.data.data.forEach((label) => {
          labelsMap[label.id] = {
            name: label.name,
            color: label.color,
          };
        });
      }
      setLabels(labelsMap);
    } catch (error) {
      console.error("Error fetching labels:", error);
    }
  };

  // Fetch developers
  const fetchDevelopers = async () => {
    try {
      const res = await Http.get("/staff-department-wise/30");
      const developersMap = {};
      if (Array.isArray(res.data)) {
        res.data.forEach((dev) => {
          const fullName = `${dev.first_name || ""} ${dev.last_name || ""}`.trim();
          developersMap[dev.id] = fullName || dev.name || `ID: ${dev.id}`;
        });
      }
      setDevelopers(developersMap);
    } catch (error) {
      console.error("Error fetching developers:", error);
      // Continue even if developers can't be fetched
    }
  };

  // Toggle story expansion
  const toggleStoryExpansion = (storyId) => {
    setExpandedStories((prev) => {
      const newState = {
        ...prev,
        [storyId]: !prev[storyId],
      };

      // Check if all stories are now expanded or collapsed
      const allStoriesExpanded = Object.values(newState).every((expanded) => expanded);

      // Only update allExpanded if individual tasks are also in the same state
      if (allStoriesExpanded === individualTasksExpanded) {
        setAllExpanded(allStoriesExpanded);
      } else if (!prev[storyId] === false) {
        // If this story is being collapsed, set allExpanded to false
        setAllExpanded(false);
      }

      return newState;
    });
  };

  // Filtering (search/project/status) is performed server-side by /backlog.
  const filteredStories = stories;

  // When "My tasks only" is on, restrict to tasks assigned to the current user.
  const matchesMyTasks = (task) => !myTasksOnly || isUserAssignedToTask(task);

  const filteredTasks = tasks.filter((task) => !task.story_id && matchesMyTasks(task));

  // Get tasks for a specific story (Done tasks only included once the
  // per-story "Show completed" toggle has lazy-loaded them).
  const getTasksForStory = (storyId) => {
    return tasks.filter(
      (task) =>
        task.story_id === storyId &&
        (task.status !== "Done" || includeDone || showCompletedStories[storyId]) &&
        matchesMyTasks(task)
    );
  };

  // Server-provided task counts for a story (Done tasks are not loaded in the
  // active view, so totals must come from the backend, not the flat list).
  const getStoryCounts = (storyId) => {
    const story = stories.find((s) => s.id === storyId);
    if (story && typeof story.tasks_total !== "undefined") {
      return { total: Number(story.tasks_total) || 0, done: Number(story.tasks_done) || 0 };
    }
    const storyTasks = tasks.filter((task) => task.story_id === storyId);
    return {
      total: storyTasks.length,
      done: storyTasks.filter((t) => t.status === "Done").length,
    };
  };

  // Calculate story status based on its tasks
  const calculateStoryStatus = (storyId) => {
    const storyTasks = tasks.filter((task) => task.story_id === storyId);
    const counts = getStoryCounts(storyId);

    if (counts.total === 0) {
      return "To Do";
    }

    if (counts.done >= counts.total) {
      return "Done";
    }

    const anyPaused = storyTasks.some((task) => task.status === "Pause");
    const anyStarted = storyTasks.some((task) => task.status === "In Progress" || task.status === "Review");

    if (anyStarted) {
      return "In Progress";
    }

    if (anyPaused) {
      // If there are paused tasks but no active ones, consider the story paused
      return "Pause";
    }

    return "To Do";
  };

  // Calculate story progress
  const calculateStoryProgress = (storyId) => {
    const storyTasks = tasks.filter((task) => task.story_id === storyId);
    const counts = getStoryCounts(storyId);

    if (counts.total === 0) {
      return { percent: 0, inProgressCount: 0, doneCount: 0, totalCount: 0 };
    }

    const inProgressCount = storyTasks.filter((task) => task.status === "In Progress" || task.status === "Review").length;
    const percent = Math.round((counts.done / counts.total) * 100);

    return { percent, inProgressCount, doneCount: counts.done, totalCount: counts.total };
  };

  // Render progress bar for stories or individual tasks
  const renderProgressBar = (storyIdOrType) => {
    let tasksToRender = [];
    const isIndividual = storyIdOrType === "individual-tasks";
    // Totals come from the server (Done/old tasks aren't loaded in active view).
    const serverCounts = isIndividual ? standaloneCounts : getStoryCounts(storyIdOrType);

    // Handle individual tasks case
    if (isIndividual) {
      tasksToRender = filteredTasks;
    } else {
      // Handle story tasks case
      tasksToRender = tasks.filter((task) => task.story_id === storyIdOrType);
    }

    if ((serverCounts ? serverCounts.total : tasksToRender.length) === 0) {
      return (
        <div style={{ display: "flex", alignItems: "center", width: "100%" }}>
          <div style={{ fontSize: "11px", marginRight: "8px", color: "#6c757d" }}>No tasks</div>
          <div
            style={{
              width: "100%",
              height: "4px",
              backgroundColor: "#e9ecef",
              borderRadius: "2px",
              overflow: "hidden",
            }}
          ></div>
        </div>
      );
    }

    // Group tasks by status. For stories the Done count and total come from
    // the server since Done tasks aren't loaded in the active view.
    const loadedDone = tasksToRender.filter((task) => task.status === "Done").length;
    const statusGroups = {
      "To Do": tasksToRender.filter((task) => task.status === "To Do").length,
      "In Progress": tasksToRender.filter((task) => task.status === "In Progress").length,
      Pause: tasksToRender.filter((task) => task.status === "Pause").length,
      Review: tasksToRender.filter((task) => task.status === "Review").length,
      Done: serverCounts ? serverCounts.done : loadedDone,
    };

    const totalCount = serverCounts ? serverCounts.total : tasksToRender.length;
    const doneCount = statusGroups["Done"];
    const percentDone = Math.round((doneCount / totalCount) * 100);

    // Calculate width for each section
    const widths = {
      Done: (statusGroups["Done"] / totalCount) * 100,
      Review: (statusGroups["Review"] / totalCount) * 100,
      "In Progress": (statusGroups["In Progress"] / totalCount) * 100,
      Pause: (statusGroups["Pause"] / totalCount) * 100,
      "To Do": (statusGroups["To Do"] / totalCount) * 100,
    };

    // Starting positions for each section
    const positions = {
      Done: 0,
      Review: widths["Done"],
      "In Progress": widths["Done"] + widths["Review"],
      Pause: widths["Done"] + widths["Review"] + widths["In Progress"],
      "To Do": widths["Done"] + widths["Review"] + widths["In Progress"] + widths["Pause"],
    };

    return (
      <div style={{ display: "flex", alignItems: "center", width: "100%" }}>
        <div style={{ fontSize: "11px", marginRight: "8px", color: "#6c757d", minWidth: "60px" }}>
          {doneCount}/{totalCount}
        </div>
        <div
          style={{
            width: "100%",
            height: "4px",
            backgroundColor: "#e9ecef",
            borderRadius: "2px",
            overflow: "hidden",
            position: "relative",
          }}
        >
          {widths["Done"] > 0 && (
            <div
              style={{
                position: "absolute",
                left: `${positions["Done"]}%`,
                top: 0,
                height: "100%",
                width: `${widths["Done"]}%`,
                backgroundColor: "#198754", // green for Done
                borderRadius: percentDone === 100 ? "2px" : "2px 0 0 2px",
              }}
            ></div>
          )}
          {widths["Review"] > 0 && (
            <div
              style={{
                position: "absolute",
                left: `${positions["Review"]}%`,
                top: 0,
                height: "100%",
                width: `${widths["Review"]}%`,
                backgroundColor: "#fd7e14", // orange for Review
              }}
            ></div>
          )}
          {widths["In Progress"] > 0 && (
            <div
              style={{
                position: "absolute",
                left: `${positions["In Progress"]}%`,
                top: 0,
                height: "100%",
                width: `${widths["In Progress"]}%`,
                backgroundColor: "#0d6efd", // blue for In Progress
              }}
            ></div>
          )}
          {widths["Pause"] > 0 && (
            <div
              style={{
                position: "absolute",
                left: `${positions["Pause"]}%`,
                top: 0,
                height: "100%",
                width: `${widths["Pause"]}%`,
                backgroundColor: "#dc3545", // red for Pause
              }}
            ></div>
          )}
        </div>
      </div>
    );
  };

  // Combine and sort stories by creation date (individual tasks shown separately)
  const combinedItems = loading
    ? []
    : [...filteredStories.map((story) => ({ ...story, itemType: "story", createdAt: new Date(story.created_at || story.updated_at || 0) }))].sort(
        (a, b) => b.createdAt - a.createdAt,
      ); // Sort by creation date, newest first

  // Handle story deletion
  const confirmDeleteStory = (story) => {
    confirmDialog({
      message: `Are you sure you want to delete the story "${story.title}"? This will NOT delete any tasks under it.`,
      header: "Confirm Deletion",
      icon: "pi pi-exclamation-triangle",
      acceptClassName: "p-button-danger",
      accept: () => handleDeleteStory(story.id),
    });
  };

  // Fetch story data for editing
  const fetchStoryForEdit = async (storyId) => {
    try {
      const response = await Http.get(`/story/${storyId}`);
      if (response.data && response.data.data) {
        setStoryDataForEdit(response.data.data);
        setEditStoryVisible(true);
      } else {
        toast.current.show({
          severity: "error",
          summary: "Error",
          detail: "Failed to fetch story data for editing",
          life: 3000,
        });
      }
    } catch (error) {
      console.error("Error fetching story data:", error);
      toast.current.show({
        severity: "error",
        summary: "Error",
        detail: error.response?.data?.message || "Failed to fetch story data for editing",
        life: 3000,
      });
    }
  };

  // Fetch task data for editing
  const fetchTaskForEdit = async (taskId) => {
    try {
      const response = await Http.get(`/task/${taskId}`);
      if (response.data && response.data.data) {
        const taskData = response.data.data;

        // Ensure dates are properly formatted for input[type="date"]
        if (taskData.start_date) {
          // First ensure it's a valid date
          const startDate = new Date(taskData.start_date);
          if (!isNaN(startDate.getTime())) {
            taskData.start_date = startDate.toISOString().split("T")[0];
          } else {
            taskData.start_date = "";
          }
        }

        if (taskData.end_date) {
          // First ensure it's a valid date
          const endDate = new Date(taskData.end_date);
          if (!isNaN(endDate.getTime())) {
            taskData.end_date = endDate.toISOString().split("T")[0];
          } else {
            taskData.end_date = "";
          }
        }

        setTaskDataForEdit(taskData);
        setEditTaskVisible(true);
      } else {
        toast.current.show({
          severity: "error",
          summary: "Error",
          detail: "Failed to fetch task data for editing",
          life: 3000,
        });
      }
    } catch (error) {
      console.error("Error fetching task data:", error);
      toast.current.show({
        severity: "error",
        summary: "Error",
        detail: error.response?.data?.message || "Failed to fetch task data for editing",
        life: 3000,
      });
    }
  };

  // Open the details modal for a task, fetching the full record (with status
  // history) since the backlog payload is trimmed.
  const openTaskDetails = async (taskId) => {
    setDetailsVisible(true);
    setDetailsLoading(true);
    setDetailsTask(null);
    try {
      const response = await Http.get(`/task/${taskId}`);
      setDetailsTask(response.data?.data || null);
    } catch (error) {
      console.error("Error fetching task details:", error);
      toast.current.show({
        severity: "error",
        summary: "Error",
        detail: "Failed to load task details.",
        life: 3000,
      });
      setDetailsVisible(false);
    } finally {
      setDetailsLoading(false);
    }
  };

  const handleDeleteStory = async (storyId) => {
    try {
      await Http.delete(`/story/${storyId}`);
      fetchData();
      toast.current.show({
        severity: "success",
        summary: "Success",
        detail: "Story deleted successfully",
        life: 3000,
      });
    } catch (error) {
      console.error("Error deleting story:", error);
      toast.current.show({
        severity: "error",
        summary: "Error",
        detail: error.response?.data?.message || "Failed to delete story",
        life: 3000,
      });
    }
  };

  // Handle task deletion
  const confirmDeleteTask = (task) => {
    confirmDialog({
      message: `Are you sure you want to delete the task "${task.title}"?`,
      header: "Confirm Deletion",
      icon: "pi pi-exclamation-triangle",
      acceptClassName: "p-button-danger",
      accept: () => handleDeleteTask(task.id),
    });
  };

  const handleDeleteTask = async (taskId) => {
    try {
      await Http.delete(`/task/${taskId}`);
      fetchData();
      toast.current.show({
        severity: "success",
        summary: "Success",
        detail: "Task deleted successfully",
        life: 3000,
      });
    } catch (error) {
      console.error("Error deleting task:", error);
      toast.current.show({
        severity: "error",
        summary: "Error",
        detail: error.response?.data?.message || "Failed to delete task",
        life: 3000,
      });
    }
  };

  // Helper functions for rendering
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString();
  };

  const getUserName = (user) => {
    if (!user) return "N/A";
    return user.name || `${user.first_name || ""} ${user.last_name || ""}`.trim() || `ID: ${user.id}`;
  };

  const renderDeveloperAvatar = (developerId) => {
    if (!developerId) return null;
    const name = developers[developerId] || "User";
    const initials = name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .substring(0, 2);

    return (
      <div
        className="avatar pm-avatar"
        title={name}
        style={{ backgroundColor: BACKLOG_AVATAR_COLORS[Number(developerId) % BACKLOG_AVATAR_COLORS.length] }}
      >
        {initials}
      </div>
    );
  };

  // Render status with appropriate color
  const renderStatus = (status) => {
    let color = "";
    switch (status) {
      case "To Do":
        color = "#6c757d"; // gray
        break;
      case "In Progress":
        color = "#0d6efd"; // blue
        break;
      case "Pause":
        color = "#dc3545"; // red
        break;
      case "Review":
        color = "#fd7e14"; // orange
        break;
      case "Done":
        color = "#198754"; // green
        break;
      default:
        color = "#6c757d"; // default gray
    }

    return (
      <span
        className="badge"
        style={{
          backgroundColor: color,
          color: "#fff",
          padding: "6px 10px",
          borderRadius: "4px",
          fontSize: "12px",
        }}
      >
        {status}
      </span>
    );
  };

  // Render label with color
  const renderLabel = (labelId) => {
    if (!labelId || !labels[labelId]) return null;
    const label = labels[labelId];

    return (
      <span
        className="pm-label-chip"
        title={label.name}
        style={{
          backgroundColor: backlogTint(label.color || "#94a3b8", 0.14),
          color: label.color || "#475569",
          borderColor: backlogTint(label.color || "#94a3b8", 0.35),
          maxWidth: "100%",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {label.name}
      </span>
    );
  };

  // Get status color
  const getStatusColor = (status) => {
    switch (status) {
      case "To Do":
        return "#6c757d"; // gray
      case "In Progress":
        return "#0d6efd"; // blue
      case "Pause":
        return "#dc3545"; // red
      case "Review":
        return "#fd7e14"; // orange
      case "Done":
        return "#198754"; // green
      default:
        return "#6c757d"; // default gray
    }
  };

  // Handle status change
  const handleStatusChange = async (itemId, newStatus, itemType) => {
    try {
      // Check if task is being moved backward in workflow
      if (itemType === "task") {
        const task = tasks.find((t) => t.id === itemId);

        // Check if the user is assigned to the task (admins bypass this)
        if (!canManageTask(task)) {
          toast.current.show({
            severity: "error",
            summary: "Permission Denied",
            detail: "You can only change status of tasks assigned to you",
            life: 3000,
          });
          return;
        }

        // Special handling for "Start" action on paused tasks
        if (task.status === "Pause" && newStatus === "Start") {
          // Get the task details with history to find previous status
          const taskResponse = await Http.get(`/task/${itemId}`);
          const taskHistory = taskResponse?.data?.data?.status_history || [];

          // Find the most recent pause entry to get the previous status
          const pauseEntry = taskHistory.find((entry) => entry.new_status === "Pause");
          let previousStatus = "In Progress"; // Default if no history found

          if (pauseEntry && pauseEntry.comment) {
            const match = pauseEntry.comment.match(/Task paused from (.*)/);
            if (match && match[1]) {
              previousStatus = match[1];
            }
          }

          // Update with the previous status
          const payload = {
            status: previousStatus,
            comment: `Task resumed to ${previousStatus} from Pause`,
          };

          const resumeResp = await Http.patch(`/task/${itemId}/status`, payload);

          // The server enforces "one active task per developer" and reports any
          // tasks it auto-paused. Reflect them locally — no extra pause calls.
          const resumeAutoPaused = resumeResp?.data?.auto_paused || [];
          if (resumeAutoPaused.length > 0) {
            toast.current.show({
              severity: "info",
              summary: "Auto-Pause",
              detail: `Automatically paused ${resumeAutoPaused.length} other active task(s)`,
              life: 3000,
            });
            setTasks((prevTasks) => prevTasks.map((t) => (resumeAutoPaused.includes(t.id) ? { ...t, status: "Pause" } : t)));
          }

          // Update local state for the resumed task
          setTasks((prevTasks) => prevTasks.map((t) => (t.id === itemId ? { ...t, status: previousStatus } : t)));

          // Highlight the updated task
          highlightItem(itemId, "task");

          toast.current.show({
            severity: "success",
            summary: "Success",
            detail: `Task resumed to ${previousStatus} status`,
            life: 3000,
          });

          return;
        }

        // Check if status transition is valid for non-paused tasks
        if (task && !isValidStatusTransition(task.status, newStatus)) {
          toast.current.show({
            severity: "error",
            summary: "Invalid Transition",
            detail: newStatus === "Pause" ? "Tasks can only be paused if they are in progress or in review" : "Invalid status transition",
            life: 3000,
          });
          return;
        }

        // Prepare additional data for status transition. Auto-pausing the
        // developer's other active tasks is now enforced by the server, so the
        // client no longer fires its own pause calls.
        let endpoint = `/task/${itemId}/status`;
        let payload = {
          status: newStatus,
          comment: "",
        };

        // If transitioning to Pause, store the current status for later return
        if (newStatus === "Pause") {
          payload.comment = `Task paused from ${task.status}`;
          payload.previous_status = task.status;
        }

        const statusResp = await Http.patch(endpoint, payload);

        // Reflect any tasks the server auto-paused (one active task per developer).
        const autoPaused = statusResp?.data?.auto_paused || [];
        if (autoPaused.length > 0) {
          toast.current.show({
            severity: "info",
            summary: "Auto-Pause",
            detail: `Automatically paused ${autoPaused.length} other active task(s)`,
            life: 3000,
          });
          setTasks((prevTasks) => prevTasks.map((t) => (autoPaused.includes(t.id) ? { ...t, status: "Pause" } : t)));
        }
      } else {
        // Handle story status changes (unchanged)
        const endpoint = `/story/${itemId}`;
        const payload = { status: newStatus };
        await Http.patch(endpoint, payload);
      }

      // Update local state instead of fetching all data again
      if (itemType === "story") {
        // For stories, update the story state
        setStories((prevStories) => prevStories.map((story) => (story.id === itemId ? { ...story, status: newStatus } : story)));
        // Highlight the updated story
        highlightItem(itemId, "story");
      } else {
        // For tasks, update the task state
        setTasks((prevTasks) => prevTasks.map((task) => (task.id === itemId ? { ...task, status: newStatus } : task)));
        // Highlight the updated task
        highlightItem(itemId, "task");
      }

      toast.current.show({
        severity: "success",
        summary: "Success",
        detail: `${itemType === "story" ? "Story" : "Task"} status updated successfully`,
        life: 3000,
      });
    } catch (error) {
      console.error(`Error updating ${itemType} status:`, error);
      toast.current.show({
        severity: "error",
        summary: "Error",
        detail: error.response?.data?.message || `Failed to update ${itemType} status`,
        life: 3000,
      });
    }
  };

  // Render all developer avatars and names
  const renderDevelopers = (developers) => {
    if (!developers || developers.length === 0) return null;

    // Limit displayed developers to 3 with a +N indicator for additional ones
    const displayLimit = 3;
    const displayedDevelopers = developers.slice(0, displayLimit);
    const remainingCount = developers.length - displayLimit;

    return (
      <div className="d-flex align-items-center justify-content-center">
        {displayedDevelopers.map((dev, index) => (
          <div
            key={dev.id}
            className="developer-badge"
            style={{
              marginRight: "2px",
              position: "relative",
            }}
          >
            {renderDeveloperAvatar(dev.id)}
          </div>
        ))}

        {/* Show indicator for additional developers */}
        {remainingCount > 0 && (
          <div
            style={{
              width: "28px",
              height: "28px",
              borderRadius: "50%",
              backgroundColor: "#6c757d",
              color: "white",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "11px",
              fontWeight: "bold",
              marginLeft: "2px",
            }}
          >
            +{remainingCount}
          </div>
        )}
      </div>
    );
  };

  // Toggle individual tasks expansion
  const toggleIndividualTasksExpansion = () => {
    const newState = !individualTasksExpanded;
    setIndividualTasksExpanded(newState);

    // Check if all stories are expanded
    const allStoriesExpanded = Object.values(expandedStories).every((expanded) => expanded);

    // Update allExpanded based on both individual tasks and stories
    if (newState === allStoriesExpanded) {
      setAllExpanded(newState);
    } else if (newState === false) {
      // If individual tasks are being collapsed, set allExpanded to false
      setAllExpanded(false);
    }
  };

  // Get status dropdown options - special handling for Paused tasks
  const getStatusDropdownOptions = (taskStatus) => {
    if (taskStatus === "Pause") {
      // For paused tasks, just show Start option
      return [{ label: "Start", value: "Start" }];
    }
    // For other statuses, show normal options
    return statusOptions;
  };

  // Render backlog item (can be story or task)
  const renderBacklogItem = (item, itemType, indentLevel = 0) => {
    const isStory = itemType === "story";
    const hasSubtasks = isStory && getTasksForStory(item.id).length > 0;
    const isDescriptionExpanded = expandedDescriptions[item.id] || false;
    const isHighlighted = highlightedItems[`${itemType}-${item.id}`] || false;
    const canModifyTask = !isStory && canManageTask(item);

    // For stories, calculate status based on tasks
    const storyStatus = isStory ? calculateStoryStatus(item.id) : null;

    // Get task type data if this is a task
    const taskType = !isStory && item.task_type_id && taskTypes[item.task_type_id] ? taskTypes[item.task_type_id] : null;

    // Current status and options
    const currentStatus = item.status || "To Do";
    const statusDropdownOptions = !isStory ? getStatusDropdownOptions(currentStatus) : [];

    return (
      <React.Fragment key={`${itemType}-${item.id}`}>
        <div
          className={`backlog-item ${isStory ? "backlog-row-story" : "backlog-row-task"}`}
          style={{
            padding: isStory ? "10px 16px" : "9px 16px",
            display: "flex",
            alignItems: "center",
            // Match the Task list highlight: translucent light-yellow that
            // gently fades, rather than a pulsing amber. Left undefined when not
            // highlighted so the stylesheet supplies the row background.
            backgroundColor: isHighlighted ? "rgba(255, 255, 0, 0.2)" : undefined,
            marginLeft: `${indentLevel * 40}px`,
            cursor: isStory ? "pointer" : "default",
            position: "relative",
            transition: isHighlighted ? "background-color 2s ease-out" : "background-color 0.15s ease",
            // Dim tasks that aren't the member's own so it's clear they're read-only.
            opacity: isStory || canModifyTask ? 1 : 0.55,
          }}
          onClick={() => isStory && toggleStoryExpansion(item.id)}
        >
          {!isStory && !canModifyTask && (
            <div
              style={{
                position: "absolute",
                top: "2px",
                right: "2px",
                fontSize: "10px",
                color: "#6c757d",
              }}
            >
              <i className="pi pi-lock" title="You cannot modify this task's status"></i>
            </div>
          )}

          {/* Toggle arrow and Type Indicator Column - Fixed width */}
          <div style={{ display: "flex", width: "60px", flexShrink: 0 }}>
            {/* Toggle arrow for stories */}
            {isStory && (
              <div style={{ width: "20px", marginRight: "8px" }}>
                {hasSubtasks ? (
                  <i className={`pi ${expandedStories[item.id] ? "pi-chevron-down" : "pi-chevron-right"}`} style={{ color: "#666" }} />
                ) : (
                  <i className="pi pi-minus" style={{ color: "#ccc" }} />
                )}
              </div>
            )}

            {/* Item Type Indicator — task types get a soft tile tinted with
                their own colour, matching the Task list. Total stays under the
                60px column: 20 (arrow) + 8 (gap) + 24 = 52px. */}
            <div style={{ width: "24px", textAlign: "center" }}>
              {isStory ? (
                <i
                  className="pi pi-bookmark"
                  style={{
                    color: hasSubtasks ? "#0747A6" : "#97A0AF",
                    fontSize: "15px",
                  }}
                ></i>
              ) : (
                <span
                  className="pm-type-icon"
                  title={taskType?.name || "Task"}
                  style={{
                    color: taskType?.color || "#4CAF50",
                    backgroundColor: backlogTint(taskType?.color || "#4CAF50", 0.12),
                  }}
                >
                  <i className={taskType?.icon || "pi pi-check-square"} />
                </span>
              )}
            </div>
          </div>

          {/* Item Title and Details Column - Flexible width */}
          <div style={{ flex: 1, minWidth: 0, paddingRight: "16px" }}>
            <div
              style={{
                fontWeight: isStory ? "600" : "500",
                fontSize: isStory ? "14px" : "13px",
                marginBottom: isStory ? "2px" : "3px",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {item.title}
              {!isStory && taskType && (
                <span
                  className="pm-label-chip"
                  style={{
                    marginLeft: "8px",
                    backgroundColor: backlogTint(taskType.color, 0.12),
                    color: taskType.color,
                    borderColor: backlogTint(taskType.color, 0.3),
                  }}
                >
                  {taskType.name}
                </span>
              )}
            </div>
            <div style={{ display: "flex", alignItems: "center", fontSize: "11px", color: "#6c757d" }}>
              {isStory ? (
                <>
                  <span style={{ marginRight: "12px" }}>
                    <i className="pi pi-folder" style={{ marginRight: "4px" }}></i>
                    {projects[item.project_id] || "N/A"}
                  </span>
                  {item.story_points && (
                    <span style={{ marginRight: "12px" }}>
                      <i className="pi pi-chart-bar" style={{ marginRight: "4px" }}></i>
                      {item.story_points} points
                    </span>
                  )}
                </>
              ) : (
                <>
                  <span style={{ marginRight: "12px" }}>
                    {item.start_date || item.end_date ? (
                      <>
                        <i className="pi pi-calendar" style={{ marginRight: "4px" }}></i>
                        {formatDate(item.start_date)} - {formatDate(item.end_date)}
                      </>
                    ) : (
                      <span className="badge bg-secondary">Unscheduled</span>
                    )}
                  </span>
                </>
              )}

              {/* Right side - Progress bar for stories, extra details for tasks */}
              {isStory ? (
                <div style={{ flex: 1, paddingRight: "16px" }}>
                  {hasSubtasks ? renderProgressBar(item.id) : <div style={{ fontSize: "11px", color: "#6c757d" }}>No tasks</div>}
                </div>
              ) : (
                <div style={{ display: "flex", alignItems: "center", marginTop: "2px", fontSize: "11px", color: "#6c757d" }}>
                  {item.description && (
                    <span
                      style={{
                        cursor: "pointer",
                        color: isDescriptionExpanded ? "#0747A6" : "#6c757d",
                        textDecoration: "underline",
                        marginRight: "12px",
                      }}
                      onClick={(e) => toggleDescriptionExpansion(item.id, e)}
                    >
                      <i className={`pi ${isDescriptionExpanded ? "pi-chevron-up" : "pi-chevron-down"}`} style={{ marginRight: "4px" }}></i>
                      {isDescriptionExpanded ? "Hide details" : "Show details"}
                    </span>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Status Column - Fixed width */}
          <div
            style={{
              width: "120px",
              flexShrink: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {!isStory ? (
              /* Dropdown for tasks */
              <div className="status-dropdown-container" onClick={(e) => e.stopPropagation()}>
                {item.status === "Pause" ? (
                  /* Custom Start button for paused tasks */
                  <button
                    onClick={() => {
                      if (canModifyTask) {
                        handleStatusChange(item.id, "Start", itemType);
                      } else {
                        toast.current.show({
                          severity: "error",
                          summary: "Permission Denied",
                          detail: "You can only change status of tasks assigned to you",
                          life: 3000,
                        });
                      }
                    }}
                    className="p-inputtext-sm"
                    disabled={!canModifyTask}
                    style={{
                      height: "24px",
                      fontSize: "11px",
                      fontWeight: 600,
                      backgroundColor: "#dc3545", // Red for Pause — stays solid: it's the one call to action
                      border: "none",
                      color: "#fff",
                      width: "100px",
                      padding: "1px 8px",
                      borderRadius: "999px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      cursor: canModifyTask ? "pointer" : "default",
                      opacity: canModifyTask ? 1 : 0.7,
                    }}
                  >
                    Start
                  </button>
                ) : (
                  /* Regular dropdown for other statuses */
                  <Dropdown
                    value={item.status}
                    options={statusDropdownOptions}
                    onChange={(e) => {
                      // Before calling handleStatusChange, check if the user can modify
                      if (!canModifyTask) {
                        toast.current.show({
                          severity: "error",
                          summary: "Permission Denied",
                          detail: "You can only change status of tasks assigned to you",
                          life: 3000,
                        });
                        return;
                      }

                      // For regular status changes, check transition validity
                      if (isValidStatusTransition(item.status, e.value) || e.value === item.status) {
                        handleStatusChange(item.id, e.value, itemType);
                      } else {
                        toast.current.show({
                          severity: "error",
                          summary: "Invalid Transition",
                          detail: "Tasks can only be moved forward in the workflow",
                          life: 3000,
                        });
                      }
                    }}
                    className="p-inputtext-sm"
                    disabled={!canModifyTask}
                    style={{
                      height: "24px",
                      fontSize: "11px",
                      fontWeight: 600,
                      backgroundColor: backlogTint(getStatusColor(item.status), 0.12),
                      border: `1px solid ${backlogTint(getStatusColor(item.status), 0.25)}`,
                      color: getStatusColor(item.status),
                      width: "100px",
                      padding: "1px 8px",
                      borderRadius: "999px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      opacity: canModifyTask ? 1 : 0.7,
                    }}
                    itemTemplate={(option) => {
                      const optionColor = !option ? "#6c757d" : option.value === "Start" ? "#16a34a" : getStatusColor(option.value);
                      return (
                        <div
                          style={{
                            padding: "4px 8px",
                            fontSize: "11px",
                            fontWeight: 600,
                            backgroundColor: backlogTint(optionColor, 0.12),
                            color: optionColor,
                            borderRadius: "999px",
                            margin: "2px 0",
                            textAlign: "center",
                            width: "100%",
                          }}
                        >
                          {option?.label || ""}
                        </div>
                      );
                    }}
                    valueTemplate={(option) => (
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          width: "100%",
                        }}
                      >
                        <span style={{ flex: 1, textAlign: "center" }}>{option?.label || item.status}</span>
                        <i className="pi pi-chevron-down" style={{ fontSize: "9px", marginLeft: "4px", opacity: 0.8 }}></i>
                      </div>
                    )}
                    panelStyle={{ width: "120px", padding: "4px" }}
                  />
                )}
              </div>
            ) : (
              // Empty placeholder for stories to maintain alignment
              <div style={{ width: "100px" }}></div>
            )}
          </div>

          {/* Label Column - Fixed width */}
          <div
            style={{
              width: "120px",
              flexShrink: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {item.label_id ? renderLabel(item.label_id) : <div style={{ width: "75px" }}></div>}
          </div>

          {/* Developers Column - Fixed width */}
          <div
            style={{
              width: "100px",
              flexShrink: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {isStory && item.developer_id ? (
              renderDeveloperAvatar(item.developer_id)
            ) : !isStory && item.developers && item.developers.length > 0 ? (
              renderDevelopers(item.developers)
            ) : (
              <div style={{ width: "28px" }}></div>
            )}
          </div>

          {/* Actions Column - Fixed width */}
          <div
            className="pm-actions"
            style={{
              width: "100px",
              flexShrink: 0,
              display: "flex",
              gap: "4px",
              justifyContent: "flex-end",
            }}
          >
            {!isStory && (
              <Button
                icon="pi pi-eye"
                className="p-button-rounded p-button-secondary p-button-sm p-button-text"
                style={{ width: "26px", height: "26px", padding: "0" }}
                onClick={(e) => {
                  e.stopPropagation();
                  openTaskDetails(item.id);
                }}
                tooltip="Details"
                tooltipOptions={{ position: "top" }}
              />
            )}
            {privileged && isStory && (
              <Button
                icon="pi pi-plus"
                className="p-button-rounded p-button-info p-button-sm p-button-text"
                style={{ width: "26px", height: "26px", padding: "0" }}
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedStory(item);
                  setCreateTaskVisible(true);
                }}
                tooltip="Add Task"
                tooltipOptions={{ position: "top" }}
              />
            )}
            {privileged && (
            <Button
              icon="pi pi-pencil"
              className="p-button-rounded p-button-success p-button-sm p-button-text"
              style={{ width: "26px", height: "26px", padding: "0" }}
              onClick={(e) => {
                e.stopPropagation();
                if (isStory) {
                  setSelectedStory(item);
                  fetchStoryForEdit(item.id);
                } else {
                  setSelectedTask(item);
                  fetchTaskForEdit(item.id);
                }
              }}
              tooltip="Edit"
              tooltipOptions={{ position: "top" }}
            />
            )}
            {/* {!isStory && (
              <Button
                icon="pi pi-sync"
                className="p-button-rounded p-button-outlined p-button-sm"
                style={{ width: "24px", height: "24px" }}
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedTask(item);
                  setStatusUpdateVisible(true);
                }}
                tooltip="Update Status"
                tooltipOptions={{ position: "top" }}
                disabled={!canModifyTask}
              />
            )} */}
            {privileged && (
            <Button
              icon="pi pi-trash"
              className="p-button-rounded p-button-danger p-button-sm p-button-text"
              style={{ width: "26px", height: "26px", padding: "0" }}
              onClick={(e) => {
                e.stopPropagation();
                if (isStory) {
                  confirmDeleteStory(item);
                } else {
                  confirmDeleteTask(item);
                }
              }}
              tooltip="Delete"
              tooltipOptions={{ position: "top" }}
            />
            )}
          </div>
        </div>

        {/* Render tasks for this story if expanded */}
        {isStory && expandedStories[item.id] && getTasksForStory(item.id).map((task) => renderBacklogItem(task, "task", 1))}

        {/* Per-story "Show completed" toggle (active view only) */}
        {isStory && expandedStories[item.id] && !includeDone && getStoryCounts(item.id).done > 0 && (
          <div style={{ padding: "6px 16px 6px 76px", borderBottom: "1px solid #e9ecef" }}>
            <span role="button" onClick={(e) => toggleStoryCompleted(item.id, e)} style={{ fontSize: "12px", color: "#0d6efd", cursor: "pointer" }}>
              <i className={`pi ${showCompletedStories[item.id] ? "pi-eye-slash" : "pi-eye"}`} style={{ fontSize: "11px", marginRight: "5px" }} />
              {showCompletedStories[item.id] ? "Hide completed tasks" : `Show ${getStoryCounts(item.id).done} completed task(s)`}
            </span>
          </div>
        )}

        {/* Show task description when expanded */}
        {!isStory && isDescriptionExpanded && item.description && (
          <div
            className="task-description"
            style={{
              padding: "12px 16px 12px 76px",
              backgroundColor: "#f8f9fa",
              borderBottom: "1px solid #e9ecef",
              marginLeft: `${indentLevel * 40}px`,
              fontSize: "13px",
              position: "relative",
              lineHeight: "1.5",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ maxWidth: "100%", overflowX: "auto" }}>
              <div style={{ marginBottom: "8px", fontWeight: "500" }}>Description:</div>
              <div dangerouslySetInnerHTML={{ __html: item.description }} />
            </div>
          </div>
        )}
      </React.Fragment>
    );
  };

  // Calculate progress specifically for individual tasks
  const renderIndividualTasksHeader = () => {
    // Accurate totals come from the server, independent of pagination.
    const individualTasksCount = standaloneCounts.total;
    const doneCount = standaloneCounts.done;
    const percentDone = individualTasksCount > 0 ? Math.round((doneCount / individualTasksCount) * 100) : 0;

    // Determine if there are any in-progress tasks
    const hasInProgressTasks = filteredTasks.some((task) => task.status === "In Progress" || task.status === "Review");

    // Determine status based on tasks
    let status = "To Do";
    if (individualTasksCount === 0) {
      status = "To Do";
    } else if (percentDone === 100) {
      status = "Done";
    } else if (hasInProgressTasks) {
      status = "In Progress";
    }

    return (
      <div
        className="backlog-item backlog-row-story"
        style={{
          padding: "10px 16px",
          display: "flex",
          alignItems: "center",
          cursor: "pointer",
          position: "relative",
          transition: "background-color 0.15s ease",
        }}
        onClick={toggleIndividualTasksExpansion}
      >
        {/* Toggle arrow column - Fixed width */}
        <div style={{ display: "flex", width: "60px", flexShrink: 0 }}>
          <div style={{ width: "20px", marginRight: "8px" }}>
            {filteredTasks.length > 0 ? (
              <i className={`pi ${individualTasksExpanded ? "pi-chevron-down" : "pi-chevron-right"}`} style={{ color: "#666" }} />
            ) : (
              <i className="pi pi-minus" style={{ color: "#ccc" }} />
            )}
          </div>
          <div style={{ width: "20px", textAlign: "center" }}>
            <i
              className="pi pi-list"
              style={{
                color: filteredTasks.length > 0 ? "#0747A6" : "#97A0AF",
                fontSize: "16px",
              }}
            ></i>
          </div>
        </div>

        {/* Title and Details Column - Flexible width */}
        <div style={{ flex: 1, minWidth: 0, paddingRight: "16px" }}>
          <div
            style={{
              fontWeight: "600",
              fontSize: "14px",
              marginBottom: "2px",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            Individual Tasks
          </div>
          <div style={{ display: "flex", alignItems: "center", fontSize: "11px", color: "#6c757d" }}>
            <span style={{ marginRight: "12px" }}>
              <i className="pi pi-check-square" style={{ marginRight: "4px" }}></i>
              {individualTasksCount} tasks
            </span>

            {/* Progress bar */}
            <div style={{ flex: 1, paddingRight: "16px" }}>
              {filteredTasks.length > 0 ? renderProgressBar("individual-tasks") : <div style={{ fontSize: "11px", color: "#6c757d" }}>No tasks</div>}
            </div>
          </div>
        </div>

        {/* Status Column - Fixed width */}
        <div
          style={{
            width: "120px",
            flexShrink: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <div style={{ width: "100px" }}></div>
        </div>

        {/* Label Column - Fixed width */}
        <div
          style={{
            width: "120px",
            flexShrink: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <div style={{ width: "75px" }}></div>
        </div>

        {/* Developers Column - Fixed width */}
        <div
          style={{
            width: "100px",
            flexShrink: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <div style={{ width: "28px" }}></div>
        </div>

        {/* Actions Column - Fixed width */}
        <div
          className="pm-actions"
          style={{
            width: "100px",
            flexShrink: 0,
            display: "flex",
            gap: "4px",
            justifyContent: "flex-end",
          }}
        >
          {privileged && (
            <Button
              icon="pi pi-plus"
              className="p-button-rounded p-button-info p-button-sm p-button-text"
              style={{ width: "26px", height: "26px", padding: "0" }}
              onClick={(e) => {
                e.stopPropagation();
                setCreateTaskVisible(true);
              }}
              tooltip="Add Task"
              tooltipOptions={{ position: "top" }}
            />
          )}
        </div>
      </div>
    );
  };

  // Main render function
  return (
    <div>
      <IsLogin />
      <Toast ref={toast} />
      <ConfirmDialog />
      <style>{statusDropdownStyle}</style>

      <div className="am-mainpanel">
        {/* The theme's .am-pagetitle is a fixed 60px-tall bar — cramming the
            filters in there was what made this look clumsy. It now holds only
            the title and the Create action; filters moved to the toolbar below,
            where they get full page width and can wrap. */}
        <div className="am-pagetitle">
          <h5 className="am-title">Backlog</h5>
          {privileged && (
            <div>
              <Menu model={createMenuItems} popup ref={createMenuRef} />
              <Button
                label="Create"
                icon="pi pi-plus"
                className="p-button-success"
                onClick={(e) => createMenuRef.current.toggle(e)}
                aria-controls="create-menu"
                aria-haspopup
              />
            </div>
          )}
        </div>

        <div className="am-pagebody">
          <div className="pm-toolbar">
            <div className="pm-toolbar-group">
              <div className="pm-field pm-field-search">
                <label className="pm-field-label">Search</label>
                <span className="p-input-icon-left w-100">
                  <i className="pi pi-search" />
                  <InputText
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Search backlog..."
                    className="w-100 p-inputtext-sm"
                  />
                </span>
              </div>

              <div className="pm-field">
                <label className="pm-field-label">Project</label>
                <Dropdown
                  value={filterProjectId}
                  onChange={(e) => setFilterProjectId(e.value)}
                  options={Object.entries(projects).map(([id, title]) => ({ label: title, value: Number(id) }))}
                  placeholder="All Projects"
                  showClear
                  filter
                  className="w-100 p-inputtext-sm"
                />
              </div>

              <div className="pm-field">
                <label className="pm-field-label">Developer</label>
                <Dropdown
                  value={filterDeveloperId}
                  onChange={(e) => {
                    setFilterDeveloperId(e.value);
                    // "My tasks only" narrows to the current user, so leaving it
                    // on while picking someone else would always return nothing.
                    if (e.value) setMyTasksOnly(false);
                  }}
                  options={Object.entries(developers)
                    .map(([id, name]) => ({ label: name, value: Number(id) }))
                    .sort((a, b) => a.label.localeCompare(b.label))}
                  placeholder="All Developers"
                  showClear
                  filter
                  filterPlaceholder="Search developer..."
                  emptyMessage="No developers"
                  className="w-100 p-inputtext-sm"
                />
              </div>

              <div className="pm-field pm-field-status">
                <label className="pm-field-label">Status</label>
                <MultiSelect
                  value={filterStatuses}
                  onChange={(e) => setFilterStatuses(e.value)}
                  options={["To Do", "In Progress", "Pause", "Review", "Done"].map((s) => ({ label: s, value: s }))}
                  placeholder="All Statuses"
                  className="w-100 p-inputtext-sm"
                  display="chip"
                />
              </div>
            </div>

            <div className="pm-toolbar-group pm-toolbar-end">
              <label className="pm-switch" title="Include completed work">
                <InputSwitch checked={includeDone} onChange={(e) => setIncludeDone(e.value)} />
                <span>Completed</span>
              </label>
              <label className="pm-switch" title="Show only tasks assigned to me">
                <InputSwitch
                  checked={myTasksOnly}
                  onChange={(e) => {
                    setMyTasksOnly(e.value);
                    // Same axis as the Developer dropdown — turning this on
                    // clears an explicit pick so the two can't contradict.
                    if (e.value) setFilterDeveloperId(null);
                  }}
                />
                <span>My tasks</span>
              </label>
              <Button
                icon={allExpanded ? "pi pi-chevron-up" : "pi pi-chevron-down"}
                className="p-button-outlined p-button-sm pm-toolbar-btn"
                onClick={toggleAllExpanded}
                tooltip={allExpanded ? "Collapse all" : "Expand all"}
                tooltipOptions={{ position: "top" }}
                aria-label={allExpanded ? "Collapse all" : "Expand all"}
              />
            </div>
          </div>

          <div className="card pd-20 pd-sm-40">
            {error && (
              <div className="alert alert-danger" role="alert">
                {error}
              </div>
            )}

            {loading ? (
              <div className="text-center p-5">
                <i className="pi pi-spin pi-spinner" style={{ fontSize: "2rem" }}></i>
                <p className="mt-2">Loading backlog...</p>
              </div>
            ) : combinedItems.length === 0 && filteredTasks.length === 0 ? (
              <div className="text-center p-5">
                <i className="pi pi-folder-open" style={{ fontSize: "3rem", color: "#ccc" }}></i>
                <p className="mt-2">No items found. Create your first story or task!</p>
              </div>
            ) : (
              <div className="backlog-container">
                {/* Column headers — widths must stay in step with renderBacklogItem */}
                <div className="pm-backlog-head">
                  {/* Toggle and Title column */}
                  <div style={{ display: "flex", flex: 1, minWidth: 0 }}>
                    <div style={{ width: "60px" }}></div>
                    <div>Title</div>
                  </div>

                  {/* Status column */}
                  <div style={{ width: "120px", textAlign: "center" }}>Status</div>

                  {/* Label column */}
                  <div style={{ width: "120px", textAlign: "center" }}>Label</div>

                  {/* Developers column */}
                  <div style={{ width: "100px", textAlign: "center" }}>Developers</div>

                  {/* Actions column */}
                  <div style={{ width: "100px", textAlign: "right" }}></div>
                </div>

                {/* Render stories sorted by creation date */}
                {combinedItems.map((item) => renderBacklogItem(item, item.itemType))}

                {storiesPagination.has_more && (
                  <div className="text-center p-3">
                    <Button
                      label={loadingMore ? "Loading..." : "Load more stories"}
                      icon="pi pi-angle-down"
                      className="p-button-outlined p-button-sm"
                      onClick={loadMoreStories}
                      disabled={loadingMore}
                    />
                  </div>
                )}

                {/* Render Individual Tasks section if there are any standalone tasks */}
                {filteredTasks.length > 0 && (
                  <>
                    {renderIndividualTasksHeader()}
                    {individualTasksExpanded && filteredTasks.map((task) => renderBacklogItem(task, "task", 1))}
                    {individualTasksExpanded && tasksPagination.has_more && (
                      <div className="text-center p-3">
                        <Button
                          label={loadingMore ? "Loading..." : "Load more tasks"}
                          icon="pi pi-angle-down"
                          className="p-button-outlined p-button-sm"
                          onClick={loadMoreTasks}
                          disabled={loadingMore}
                        />
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Dialogs */}
      <Dialog
        header="Task Details"
        visible={detailsVisible}
        style={{ width: "80vw" }}
        onHide={() => setDetailsVisible(false)}
        maximizable
      >
        {detailsLoading ? (
          <div className="text-center p-5">
            <i className="pi pi-spin pi-spinner" style={{ fontSize: "2rem" }}></i>
            <p className="mt-2">Loading task details...</p>
          </div>
        ) : (
          <TaskDetails task={detailsTask} showActions={false} />
        )}
      </Dialog>

      <Dialog
        header="Create Story"
        visible={createStoryVisible}
        style={{ width: "80%", maxWidth: "900px" }}
        onHide={() => setCreateStoryVisible(false)}
      >
        <CreateStory
          setVisible={setCreateStoryVisible}
          onSuccess={(newStoryData) => {
            // Add new story directly to the state instead of refreshing all data
            if (newStoryData) {
              handleAddNewStory(newStoryData);
            }
            setCreateStoryVisible(false);
            toast.current.show({
              severity: "success",
              summary: "Success",
              detail: "Story created successfully",
              life: 3000,
            });
          }}
        />
      </Dialog>

      <Dialog
        header="Edit Story"
        visible={editStoryVisible}
        style={{ width: "80%", maxWidth: "900px" }}
        onHide={() => {
          setEditStoryVisible(false);
          setStoryDataForEdit(null);
        }}
      >
        {storyDataForEdit && (
          <EditStory
            rowData={storyDataForEdit}
            setVisible={setEditStoryVisible}
            onSuccess={(updatedStoryData) => {
              // Update story directly in the state without refreshing all data
              if (updatedStoryData) {
                handleUpdateStory(updatedStoryData);
              }
              setEditStoryVisible(false);
              setStoryDataForEdit(null);
              toast.current.show({
                severity: "success",
                summary: "Success",
                detail: "Story updated successfully",
                life: 3000,
              });
            }}
          />
        )}
      </Dialog>

      <Dialog
        header="Create Task"
        visible={createTaskVisible}
        style={{ width: "80%", maxWidth: "900px" }}
        onHide={() => {
          setCreateTaskVisible(false);
          setSelectedStory(null);
        }}
      >
        <CreateTask
          initialStoryId={selectedStory?.id}
          onSuccess={(newTaskData) => {
            // Add new task directly to the state instead of refreshing all data
            if (newTaskData) {
              handleAddNewTask(newTaskData);
            }
            setCreateTaskVisible(false);
            toast.current.show({
              severity: "success",
              summary: "Success",
              detail: "Task created successfully",
              life: 3000,
            });
          }}
        />
      </Dialog>

      <Dialog
        header="Edit Task"
        visible={editTaskVisible}
        style={{ width: "80%", maxWidth: "900px" }}
        onHide={() => {
          setEditTaskVisible(false);
          setTaskDataForEdit(null);
        }}
      >
        {taskDataForEdit && (
          <>
            {!canManageTask(taskDataForEdit) ? (
              <div className="alert alert-warning">
                <i className="pi pi-exclamation-triangle mr-2"></i>
                You cannot edit this task because you are not assigned to it.
              </div>
            ) : (
              <EditTask
                taskId={taskDataForEdit.id}
                setVisible={setEditTaskVisible}
                onSuccess={(updatedTaskData) => {
                  // Update task directly in the state without refreshing all data
                  if (updatedTaskData) {
                    handleUpdateTask(updatedTaskData);
                  }
                  setEditTaskVisible(false);
                  setTaskDataForEdit(null);
                  toast.current.show({
                    severity: "success",
                    summary: "Success",
                    detail: "Task updated successfully",
                    life: 3000,
                  });
                }}
              />
            )}
          </>
        )}
      </Dialog>

      <Dialog header="Update Task Status" visible={statusUpdateVisible} style={{ width: "500px" }} onHide={() => setStatusUpdateVisible(false)}>
        {selectedTask && (
          <>
            {!canManageTask(selectedTask) ? (
              <div className="alert alert-warning">
                <i className="pi pi-exclamation-triangle mr-2"></i>
                You cannot change the status of this task because you are not assigned to it.
              </div>
            ) : (
              <StatusUpdate
                taskId={selectedTask.id}
                currentStatus={selectedTask.status}
                validStatusTransitions={statusOptions
                  .filter((option) => option.value === selectedTask.status || isValidStatusTransition(selectedTask.status, option.value))
                  .map((option) => option.value)}
                onSuccess={(updatedTaskData) => {
                  // Update task directly in the state without refreshing all data
                  if (updatedTaskData) {
                    handleUpdateTask(updatedTaskData);
                  }
                  setStatusUpdateVisible(false);
                  toast.current.show({
                    severity: "success",
                    summary: "Success",
                    detail: "Task status updated successfully",
                    life: 3000,
                  });
                }}
                setVisible={setStatusUpdateVisible}
              />
            )}
          </>
        )}
      </Dialog>

    </div>
  );
};

export default Backlog;
